import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { pick } from "lodash"
import api, { GenericError } from "../api"
import { ConsolidationRequest, MultiDimsUpdateRequest } from "../api/consolidation.api"

export interface ConsolidationsListState {
    consolidations: Record<string, any>
    consolidated: Record<string, any>[]
    loading: boolean
    error?: GenericError
    consolBox?: Record<string, any>
    success?: boolean
    barcode?: string
}

const initialState: ConsolidationsListState = {
    consolidations: {},
    consolidated: [],
    loading: false
}

export const loadConsolidations = createAsyncThunk(
    "consolidations/list",
    async ({ warehouse }: { warehouse?: string }, thunkAPI) => {
        try {
            return await api.consolidation.consolidationsList({ warehouse })
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const loadConsolidated = createAsyncThunk(
    "consolidated/list",
    async ({ warehouse }: { warehouse?: string }, thunkAPI) => {
        try {
            return await api.consolidation.consolidatedList({ warehouse })
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const multiBoxConsolidation = createAsyncThunk(
    "consolidations/mult-box-consolidation",
    async (consolidationData: ConsolidationRequest, thunkAPI) => {
        try {
            return await api.consolidation.multiBoxConsolidation(consolidationData)
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const partialConsolidation = createAsyncThunk(
    "consolidations/partial-consolidation",
    async (consolidationData: ConsolidationRequest, thunkAPI) => {
        try {
            return await api.consolidation.partialConsolidation(consolidationData)
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const multiDimsUpdate = createAsyncThunk(
    "consolidations/multi-dims-update",
    async (multiDimsUpdateData: MultiDimsUpdateRequest, thunkAPI) => {
        try {
            return await api.consolidation.multiDimsUpdate(multiDimsUpdateData)
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const consolidationsSlice = createSlice({
    name: "consolidations",
    initialState,

    reducers: {
        clearError: (state: ConsolidationsListState) => {
            state.error = undefined
        },
        setConsolBox: (state: ConsolidationsListState, action: PayloadAction<any>) => {
            state.consolBox = action.payload || {}
        },
        resetSuccess: (state: ConsolidationsListState) => {
            state.success = undefined
        }
    },

    extraReducers: {
        [loadConsolidations.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadConsolidations.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [loadConsolidations.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.consolidations = action.payload?.data?.payload || {}
        },

        [loadConsolidated.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadConsolidated.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [loadConsolidated.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.consolidated = action.payload?.data?.payload || {}
        },

        [multiBoxConsolidation.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [multiBoxConsolidation.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [multiBoxConsolidation.fulfilled.toString()]: (state, action) => {
            const payload = action.payload?.data?.payload || {}
            state.loading = false
            state.success = true
            state.barcode = payload?.barcode_page || ""
            state.consolBox = {
                uuid: payload?.shipment?.uuid,
                id: payload?.shipment?.id,
                Children: [
                    {
                        ...pick(payload?.shipment, [
                            "id",
                            "uuid",
                            "dimension_height",
                            "dimension_width",
                            "dimension_length",
                            "dimensions_unit",
                            "weight_value",
                            "weight_unit"
                        ])
                    },
                    ...payload?.shipment?.secondary_boxes?.map((sb: Record<string, any>) => ({
                        ...pick(sb, [
                            "id",
                            "uuid",
                            "dimension_height",
                            "dimension_width",
                            "dimension_length",
                            "dimensions_unit",
                            "weight_value",
                            "weight_unit"
                        ])
                    }))
                ]
            }
        },

        [partialConsolidation.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [partialConsolidation.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [partialConsolidation.fulfilled.toString()]: (state, action) => {
            const payload = action.payload?.data?.payload || {}
            state.loading = false
            state.success = true
            state.barcode = payload?.barcode_page || ""
            state.consolBox = {
                uuid: payload?.shipment?.uuid,
                id: payload?.shipment?.id,
                Children: [
                    {
                        ...pick(payload?.shipment, [
                            "id",
                            "uuid",
                            "dimension_height",
                            "dimension_width",
                            "dimension_length",
                            "dimensions_unit",
                            "weight_value",
                            "weight_unit"
                        ])
                    },
                    ...payload?.shipment?.secondary_boxes?.map((sb: Record<string, any>) => ({
                        ...pick(sb, [
                            "id",
                            "uuid",
                            "dimension_height",
                            "dimension_width",
                            "dimension_length",
                            "dimensions_unit",
                            "weight_value",
                            "weight_unit"
                        ])
                    }))
                ]
            }
        },

        [multiDimsUpdate.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [multiDimsUpdate.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.payload) {
                state.error = { payload: action.payload?.response?.data?.payload }
            } else {
                state.error = { error: action.payload?.response?.data?.error }
            }
        },
        [multiDimsUpdate.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.success = true
            state.barcode = action.payload?.data?.payload?.barcode_page
        }
    }
})

export const { clearError, setConsolBox, resetSuccess } = consolidationsSlice.actions

export default consolidationsSlice.reducer
